/**
 * @fileoverview Backward-compat wrapper around the unified
 * `FilePreviewDialog`.
 *
 * The original implementation (header / footer / per-MIME renderer
 * dispatch) lived here as ~430 lines duplicated across three other
 * preview surfaces. That logic now lives in `components/file-preview/`
 * — this file just maps the legacy `attachment` prop to a `FileSource`
 * via {@link urlSource} and forwards.
 *
 * Kept as a thin wrapper (rather than deleted outright) so the dozens
 * of importers across the chat UI don't have to change in one big
 * sweep. Mark deprecated and remove once those imports migrate.
 */
import { useMemo } from "react";

import { FilePreviewDialog, urlSource } from "@/components/file-preview";
import type { Attachment } from "@/types/chat-message";

interface AttachmentPreviewDialogProps {
  /** The attachment to preview. */
  attachment: Attachment;
  /** Whether the dialog is open. */
  open: boolean;
  /** Callback when dialog open state changes. */
  onOpenChange: (open: boolean) => void;
}

/**
 * @deprecated Construct a `FileSource` (via `urlSource`) and use
 * `FilePreviewDialog` directly. This wrapper exists only to keep
 * existing chat callers working during the migration.
 */
export function AttachmentPreviewDialog({
  attachment,
  open,
  onOpenChange,
}: AttachmentPreviewDialogProps) {
  /**
   * Memoize the source so its identity is stable across re-renders that
   * don't change the underlying attachment. Renderer hooks key effects
   * on `source.id`, but we'd still recreate handlers + leak object
   * literals every render without this — and the dialog close animation
   * would unmount source-dependent state mid-fade.
   *
   * The source is built regardless of `open` (no conditional null) so
   * the close animation can replay the same content rather than seeing
   * the children disappear instantly.
   */
  const source = useMemo(
    () =>
      urlSource({
        url: attachment.url,
        filename: attachment.name,
        contentType: attachment.contentType,
        size: attachment.size,
        fileId: attachment.fileId,
      }),
    [
      attachment.url,
      attachment.name,
      attachment.contentType,
      attachment.size,
      attachment.fileId,
    ],
  );

  return (
    <FilePreviewDialog
      source={source}
      open={open}
      onOpenChange={onOpenChange}
    />
  );
}
